/* ──────────────────────────────────────────────
 *  Panier unique : sélection du tunnel à son ouverture (spec Parcours § 6 et § 7.8).
 *
 *  - Prestation choisie sur une fiche (`incoming`) : c'est le dernier geste du client, elle est
 *    toujours sélectionnée. Multi-prestation désactivé : elle remplace tout. Activé : elle complète
 *    la sélection de la même visite ; une sélection d'une visite précédente est proposée par le
 *    bandeau (« Ajouter à votre sélection précédente » / « Remplacer »).
 *  - Sans fiche : la sélection de la même visite est reprise telle quelle ; celle d'une visite
 *    précédente n'est jamais réinjectée en silence (bandeau « Reprendre / Recommencer »).
 *  - La présélection du builder (`preselect_ids`) complète la sélection, elle ne l'écrase plus.
 * ────────────────────────────────────────────── */

import { isAvailablePriceOption, PrestationType } from '@cfixe-monorepo/shared';
import type { BookingEntryMode, BookingPreselectLine, BookingPrestation, CartItem } from '../types';
import { hasChoicesBeyondPrice } from '../utils/prestation-choices';
import type { BookingCart, BookingCartLine } from './booking-cart-store';
import { mergeCartItems, rebuildBookingCartItems } from './booking-cart-lines';

export interface CartEntryInput {
  cart: BookingCart;
  /** La sélection enregistrée vient de la visite en cours (`isBookingCartFromThisVisit`). */
  sameVisit: boolean;
  /** Prestation choisie sur une fiche du site pendant cette visite. */
  incoming?: BookingCartLine;
  preselectIds?: readonly number[];
  /** Présélections d'un lien avec formule et quantité (`?ps=12:45:2`). */
  preselectLines?: readonly BookingPreselectLine[];
  prestations: readonly BookingPrestation[];
  multiPrestationAllowed: boolean;
  /** Forçage du mode par le lien (`?entry=`). */
  forcedMode?: Exclude<BookingEntryMode, 'multi'>;
}

/** Sélection d'une visite précédente, mise de côté jusqu'à la réponse du client au bandeau. */
export interface RestorableSelection {
  lines: BookingCartLine[];
  updated_at: number;
  items: CartItem[];
  dropped: string[];
  unbookable: string[];
}

export interface CartEntry {
  items: CartItem[];
  /** Prestations dont la formule n'est plus réservable (bandeau `preselect_unavailable`). */
  dropped: string[];
  /** Prestations qu'aucun planning ne rend réservables en ligne (bandeau `preselect_not_bookable`). */
  unbookable: string[];
  /** Une seule prestation complète (fiche, lien, catalogue d'une seule prestation) : l'étape prestations est sautée. */
  skipServices: boolean;
  /** Mode du parcours (spec Parcours § 5). */
  entryMode: BookingEntryMode;
  restorable?: RestorableSelection;
}

/**
 * Prestation réservable sans rien à choisir : une seule formule proposable, et ni supplément, ni nombre de
 * personnes, ni quantité — mêmes critères que la carte prestation (`hasChoicesBeyondPrice`). Une prestation que
 * la carte déplierait n'envoie jamais le client directement au créneau : la réservation partirait pour une personne,
 * sans supplément, quels que soient ses besoins (spec Parcours § 3 cas 3 et 11).
 */
const isCompletePrestation = (prestation: BookingPrestation): boolean =>
  prestation.is_bookable &&
  prestation.prices.filter(isAvailablePriceOption).length === 1 &&
  !hasChoicesBeyondPrice(prestation);

/**
 * Ligne d'un lien complète : formule désignée (ou prestation qui n'en propose qu'une) ET rien d'autre à choisir. Un
 * lien ne porte ni supplément ni nombre de personnes : s'ils sont proposés, le client les règle sur la carte. Une
 * ligne venue d'une fiche les porte déjà ; elle ne passe pas par ici (`fromFiche`).
 */
const isCompleteLine = (line: BookingPreselectLine, item: CartItem): boolean =>
  line.price_id !== undefined ? !hasChoicesBeyondPrice(item.prestation) : isCompletePrestation(item.prestation);

/**
 * `?ps=` de `/book` et `preselect-ids` du widget : `prestation_id[:price_id[:quantité]]`, séparés par des
 * virgules. Les entrées illisibles sont ignorées.
 */
export function parsePreselectParam(value: string | null | undefined): BookingPreselectLine[] {
  if (!value) return [];
  return value
    .split(',')
    .map((entry): BookingPreselectLine | undefined => {
      const [prestationId, priceId, quantity] = entry
        .trim()
        .split(':')
        .map((part): number => Number.parseInt(part, 10));
      if (!Number.isInteger(prestationId) || prestationId <= 0) return undefined;
      return {
        prestation_id: prestationId,
        ...(Number.isInteger(priceId) && priceId > 0 ? { price_id: priceId } : {}),
        ...(Number.isInteger(quantity) && quantity > 1 ? { quantity } : {}),
      };
    })
    .filter((line): line is BookingPreselectLine => line !== undefined);
}

export function resolveCartEntry(input: CartEntryInput): CartEntry {
  const { cart, sameVisit, prestations, multiPrestationAllowed: multi } = input;
  const limit = (items: CartItem[]): CartItem[] => (multi ? items : items.slice(0, 1));
  const stored = rebuildBookingCartItems(cart.lines, prestations);
  const arriving = input.incoming
    ? rebuildBookingCartItems([input.incoming], prestations)
    : { items: [], dropped: [], unbookable: [] };

  let items: CartItem[] = [];
  const dropped = [...arriving.dropped];
  const unbookable = [...arriving.unbookable];
  let restorable: RestorableSelection | undefined;
  const asRestorable = (): RestorableSelection => ({ lines: cart.lines, updated_at: cart.updated_at, ...stored });

  if (arriving.items.length > 0) {
    items = multi ? mergeCartItems(sameVisit ? stored.items : [], arriving.items) : arriving.items;
    // Multi désactivé : remplacer est le comportement attendu, sans question.
    if (multi && !sameVisit && stored.items.length > 0) restorable = asRestorable();
  } else if (sameVisit) {
    // Même visite : la sélection est reprise, et chaque ligne écartée est signalée — même quand aucune ne survit,
    // sinon la règle « une seule prestation réservable » la remplacerait en silence.
    items = limit(stored.items);
    dropped.push(...stored.dropped);
    unbookable.push(...stored.unbookable);
  } else if (stored.items.length > 0) {
    restorable = asRestorable();
  }

  // Bandeau resté sans réponse pendant cette visite (page rechargée) : la même question revient.
  if (!restorable && cart.restorable && (multi || items.length === 0)) {
    const pending = rebuildBookingCartItems(cart.restorable.lines, prestations);
    if (pending.items.length > 0) restorable = { ...cart.restorable, ...pending };
  }

  const preselectLines: BookingPreselectLine[] = [
    ...(input.preselectLines ?? []),
    ...(input.preselectIds ?? []).map((prestationId): BookingPreselectLine => ({ prestation_id: prestationId })),
  ];
  let completeLink = false;
  for (const line of preselectLines) {
    if (!multi && items.length > 0) break;
    if (items.some((item): boolean => item.prestation.prestation_id === line.prestation_id)) continue;
    const preselected = rebuildBookingCartItems(
      [
        {
          prestation_id: line.prestation_id,
          price_id: line.price_id,
          quantity: line.quantity ?? 1,
          supplement_ids: [],
        },
      ],
      prestations,
    );
    items.push(...preselected.items);
    dropped.push(...preselected.dropped);
    unbookable.push(...preselected.unbookable);
    completeLink = preselected.items.length === 1 && isCompleteLine(line, preselected.items[0]);
  }

  // Catalogue d'une seule prestation réservable, complète : rien à choisir, le parcours commence au créneau.
  // Jamais après une ligne écartée (fiche, lien ou sélection enregistrée) : écartée et signalée, jamais remplacée —
  // le parcours direct ne charge d'abord que la prestation de la fiche, et le bandeau « choisissez-en une autre »
  // accompagnerait sinon une autre formule déjà au panier. Le client choisit lui-même.
  const bookable = prestations.filter(
    (prestation): boolean => prestation.is_bookable && prestation.type !== PrestationType.RIDE,
  );
  if (
    items.length === 0 &&
    dropped.length === 0 &&
    unbookable.length === 0 &&
    !restorable &&
    bookable.length === 1 &&
    isCompletePrestation(bookable[0])
  ) {
    const only = rebuildBookingCartItems(
      [{ prestation_id: bookable[0].prestation_id, quantity: 1, supplement_ids: [] }],
      prestations,
    ).items;
    items.push(...only);
    completeLink = only.length === 1;
  }

  const fromFiche = arriving.items.length > 0 && arriving.dropped.length === 0 && arriving.unbookable.length === 0;
  // Une ligne écartée (formule ou prestation non réservable) interdit de sauter l'étape prestations : le client
  // y voit le bandeau et rechoisit.
  const single = items.length === 1 && dropped.length === 0 && unbookable.length === 0 && !restorable;
  const express =
    input.forcedMode === 'standard' ? false : single && (fromFiche || completeLink || input.forcedMode === 'express');
  const entryMode: BookingEntryMode = multi && items.length >= 2 ? 'multi' : express ? 'express' : 'standard';

  return {
    items,
    dropped,
    unbookable,
    skipServices: express,
    entryMode,
    ...(restorable ? { restorable } : {}),
  };
}

/**
 * Prestation seule à charger d'abord (spec Parcours § 9, performance express) : une fiche ou un lien d'une seule
 * prestation, sans sélection enregistrée ni mise de côté ni filtre du builder. Le catalogue complet suit en
 * arrière-plan ; sans cible, il est chargé d'emblée.
 */
export function expressCatalogueTarget(
  cart: BookingCart,
  filters:
    | {
        prestation_ids?: readonly number[];
        preselect_ids?: readonly number[];
        preselect_lines?: readonly BookingPreselectLine[];
      }
    | undefined,
  incoming: BookingCartLine | undefined,
): number | undefined {
  if (cart.lines.length > 0 || cart.restorable || filters?.prestation_ids?.length || filters?.preselect_ids?.length) {
    return undefined;
  }
  const links = filters?.preselect_lines ?? [];
  if (incoming) return links.length === 0 ? incoming.prestation_id : undefined;
  return links.length === 1 ? links[0].prestation_id : undefined;
}

/**
 * Réponse « Reprendre » / « Ajouter à votre sélection précédente » : la sélection mise de côté rejoint la
 * sélection en cours (sans doublon), ou la remplace quand le multi-prestation est désactivé.
 */
export const resumeRestorableSelection = (
  current: readonly CartItem[],
  restorable: RestorableSelection,
  multiPrestationAllowed: boolean,
): CartItem[] => (multiPrestationAllowed ? mergeCartItems(restorable.items, current) : restorable.items.slice(0, 1));
